import { useId, useState, type FormEvent } from "react";
import { TextField, TextAreaField, SelectField, CheckboxField } from "./form/FormControls";
import { ActionButton } from "./Button";
import { contactCategories, contactFieldLimits } from "@/content/contact";
import { siteConfig } from "@/config/site";
import "./ContactForm.css";

interface FormState {
  name: string;
  email: string;
  organisation: string;
  category: string;
  message: string;
  consent: boolean;
  website: string; // honeypot — must stay empty
}

const initialState: FormState = {
  name: "",
  email: "",
  organisation: "",
  category: contactCategories[0]!.value,
  message: "",
  consent: false,
  website: "",
};

type SubmitStatus = "idle" | "submitting" | "success" | "unavailable" | "invalid" | "error";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validate(state: FormState): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!state.name.trim()) errors.name = "Enter your name.";
  else if (state.name.length > contactFieldLimits.name) errors.name = "Name is too long.";

  if (!state.email.trim()) errors.email = "Enter your email address.";
  else if (!EMAIL_PATTERN.test(state.email)) errors.email = "Enter a valid email address.";
  else if (state.email.length > contactFieldLimits.email) errors.email = "Email is too long.";

  if (state.organisation.length > contactFieldLimits.organisation) {
    errors.organisation = "Organisation name is too long.";
  }

  if (!state.message.trim()) errors.message = "Enter a message.";
  else if (state.message.length > contactFieldLimits.message) {
    errors.message = `Message must be ${contactFieldLimits.message} characters or fewer.`;
  }

  if (!state.consent) errors.consent = "Please acknowledge the privacy notice to continue.";

  return errors;
}

export function ContactForm() {
  const [state, setState] = useState<FormState>(initialState);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<SubmitStatus>("idle");
  const statusId = useId();

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setState((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (state.website) {
      // Honeypot tripped: silently accept without sending anywhere real.
      setStatus("success");
      return;
    }

    const validationErrors = validate(state);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      setStatus("invalid");
      return;
    }

    setStatus("submitting");
    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: state.name,
          email: state.email,
          organisation: state.organisation,
          category: state.category,
          message: state.message,
          consent: state.consent,
          website: state.website,
        }),
      });

      if (response.status === 200) {
        setStatus("success");
        setState(initialState);
        return;
      }

      if (response.status === 503) {
        setStatus("unavailable");
        return;
      }

      if (response.status === 400) {
        const body = (await response.json().catch(() => null)) as {
          fields?: Record<string, string>;
        } | null;
        if (body?.fields) setErrors(body.fields);
        setStatus("invalid");
        return;
      }

      setStatus("error");
    } catch {
      setStatus("error");
    }
  }

  if (status === "success") {
    return (
      <div className="form-status form-status-success" role="status" id={statusId}>
        <h3>Message sent</h3>
        <p>Thank you. Devory has received your message and will get back to you.</p>
      </div>
    );
  }

  if (status === "unavailable") {
    return (
      <div className="form-status form-status-warning" role="status" id={statusId}>
        <h3>Message delivery isn&rsquo;t connected yet</h3>
        <p>
          Devory validated your message, but automated delivery isn&rsquo;t configured on this
          deployment yet. Please email{" "}
          <a href={`mailto:${siteConfig.contactEmail}`}>{siteConfig.contactEmail}</a> directly in
          the meantime. Nothing was silently dropped, and no fake confirmation is being shown here.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate aria-describedby={statusId}>
      {status === "error" ? (
        <div className="form-status form-status-error" role="alert" id={statusId}>
          <p>
            Something went wrong sending your message. Please try again, or email{" "}
            <a href={`mailto:${siteConfig.contactEmail}`}>{siteConfig.contactEmail}</a>.
          </p>
        </div>
      ) : null}

      <TextField
        id="contact-name"
        label="Name"
        required
        value={state.name}
        maxLength={contactFieldLimits.name}
        autoComplete="name"
        error={errors.name}
        onChange={(event) => update("name", event.target.value)}
      />
      <TextField
        id="contact-email"
        label="Email"
        type="email"
        required
        value={state.email}
        maxLength={contactFieldLimits.email}
        autoComplete="email"
        error={errors.email}
        onChange={(event) => update("email", event.target.value)}
      />
      <TextField
        id="contact-organisation"
        label="Organisation"
        value={state.organisation}
        maxLength={contactFieldLimits.organisation}
        autoComplete="organization"
        hint="Optional."
        error={errors.organisation}
        onChange={(event) => update("organisation", event.target.value)}
      />
      <SelectField
        id="contact-category"
        label="What is this about?"
        required
        value={state.category}
        options={contactCategories}
        onChange={(event) => update("category", event.target.value)}
      />
      <TextAreaField
        id="contact-message"
        label="Message"
        required
        value={state.message}
        maxLength={contactFieldLimits.message}
        error={errors.message}
        onChange={(event) => update("message", event.target.value)}
      />
      <div className="form-honeypot" aria-hidden="true">
        <label htmlFor="contact-website">Leave this field empty</label>
        <input
          id="contact-website"
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={state.website}
          onChange={(event) => update("website", event.target.value)}
        />
      </div>
      <CheckboxField
        id="contact-consent"
        required
        error={errors.consent}
        checked={state.consent}
        onChange={(event) => update("consent", event.target.checked)}
        label={
          <>
            I understand Devory will use this information to respond to my enquiry, as described in
            the <a href="/legal/privacy">Privacy Policy</a>.
          </>
        }
      />
      <ActionButton type="submit" disabled={status === "submitting"} block>
        {status === "submitting" ? "Sending…" : "Send message"}
      </ActionButton>
    </form>
  );
}
